import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/flexible/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

const URL = 'admin/bulk_imports';

export const adminBulkImportsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminBulkImports', params] as const,
  show: (id: string) => ['adminBulkImports', id] as const,
};

export enum BulkImportType {
  USERS = 'users',
  LOCATIONS = 'locations',
  PLAN_TYPES = 'plan_types',
  PREFERRED_PROVIDERS = 'preferred_providers',
}

export const BULK_IMPORT_TYPE_LABELS: Record<string, string> = {
  [BulkImportType.USERS]: 'Users',
  [BulkImportType.LOCATIONS]: 'Locations & Groups',
  [BulkImportType.PLAN_TYPES]: 'Plan Types & Plans',
  [BulkImportType.PREFERRED_PROVIDERS]: 'Preferred Providers',
};

export enum BulkImportStatus {
  PREVIEWING = 'previewing',
  PREVIEWED = 'previewed',
  APPLYING = 'applying',
  COMPLETED = 'completed',
  ERRORED = 'errored',
  CANCELED = 'canceled',
}

export enum RowDecision {
  WILL_CREATE = 'will_create',
  WILL_MATCH = 'will_match',
  ERROR = 'error',
}

export type BulkImportSummaryMessage = {
  message: string;
  type: 'error' | 'warning';
  rowNumbers: number[];
  totalCount: number;
};

export type BulkImportApplyTally = {
  processed: number;
  total: number;
  created: number;
  matched: number;
  failed: number;
};

export type BulkImportSummary = {
  totalRows?: number;
  willCreate?: number;
  willMatch?: number;
  error?: number;
  messages?: BulkImportSummaryMessage[];
  fileErrors?: string[];
  apply?: BulkImportApplyTally;
};

export type BulkImportRow = {
  id: string;
  rowNumber: number;
  decision: RowDecision;
  error: string | null;
  result: string | null;
  data: Record<string, any>;
  matchedRecordId: string | null;
};

export type BulkImportOptions = {
  id: string;
  importType: string;
  status: BulkImportStatus;
  sendInvite: boolean;
  summary: BulkImportSummary;
  jobProcessId: string | null;
  createdAt: string | null;
  rows?: BulkImportRow[];
};

export class BulkImport {
  id!: string;
  importType!: string;
  status!: BulkImportStatus;
  sendInvite!: boolean;
  summary!: BulkImportSummary;
  jobProcessId!: string | null;
  createdAt!: string | null;
  rows!: BulkImportRow[];

  constructor(opts: BulkImportOptions) {
    Object.assign(this, opts);
    this.summary = opts.summary ?? {};
    this.rows = opts.rows ?? [];
  }

  get previewed() {
    return this.status === BulkImportStatus.PREVIEWED;
  }

  get applying() {
    return this.status === BulkImportStatus.APPLYING;
  }

  get completed() {
    return this.status === BulkImportStatus.COMPLETED;
  }

  get errored() {
    return this.status === BulkImportStatus.ERRORED;
  }

  get canceled() {
    return this.status === BulkImportStatus.CANCELED;
  }

  get isUsers() {
    return this.importType === BulkImportType.USERS;
  }

  get typeLabel() {
    return BULK_IMPORT_TYPE_LABELS[this.importType] ?? this.importType;
  }

  get fileErrors() {
    return this.summary.fileErrors ?? [];
  }

  get actionableRowCount() {
    return this.rows.filter((row) => row.decision !== RowDecision.ERROR).length;
  }
}

export type AdminBulkImportIndexParams = BaseIndexQueryParams & {
  client?: string;
  status?: string;
};

export type CreateBulkImportParams = {
  importType: string;
  signedId: string;
  sendInvite: boolean;
};

export async function adminIndexBulkImports(
  params: AdminBulkImportIndexParams,
  signal?: GenericAbortSignal
): Promise<Paginated<BulkImport>> {
  return http
    .get<Paginated<BulkImportOptions>>(URL, { params, signal })
    .then((res) => ({ ...res.data, data: res.data.data.map((x) => new BulkImport(x)) }));
}

export async function adminShowBulkImport(id: string, signal?: GenericAbortSignal): Promise<BulkImport> {
  return http.get<BulkImportOptions>(`${URL}/${id}`, { signal }).then((res) => new BulkImport(res.data));
}

export async function createBulkImport(params: CreateBulkImportParams): Promise<BulkImport> {
  return http.post<BulkImportOptions>(URL, params).then((res) => new BulkImport(res.data));
}

export async function applyBulkImport(id: string): Promise<BulkImport> {
  return http
    .patch<BulkImportOptions>(`${URL}/${id}`, { invokeAction: 'apply' })
    .then((res) => new BulkImport(res.data));
}

export async function cancelBulkImport(id: string): Promise<BulkImport> {
  return http
    .patch<BulkImportOptions>(`${URL}/${id}`, { invokeAction: 'cancel' })
    .then((res) => new BulkImport(res.data));
}

export async function sendBulkImportInvites(id: string): Promise<BulkImport> {
  return http.post<BulkImportOptions>(`${URL}/${id}/send_invites`).then((res) => new BulkImport(res.data));
}
